/**
 * KeyItemPicker — ô chọn MỘT món trọng điểm: bấm vào là xổ cả danh sách món,
 * gõ vài chữ là lọc (không dấu, theo tên lẫn mã, gợi ý "gần giống" lệch một
 * chữ — cùng bộ tìm với ô tìm gói khi lên đơn, features/products/packageSearch).
 *
 * Chọn xong máy tự điền mã + tên, người nhập không phải gõ mã. Món GNPM chưa có
 * trong danh sách (chưa ai mua lần nào) thì có dòng "Gõ tay món chưa có…" mở lại
 * hai ô mã + tên như cũ (CEO chốt 1a 31/10/2026).
 */
import { Fragment, useId, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/ui/input";
import { cn } from "@/lib/utils";
import { matchPackages, type SearchHit } from "@/features/products/packageSearch";
import { ServiceThumb } from "@/features/cskh/tabs/bookingServiceImage";
import type { KeyItemOption } from "../keyItemOptions";

type Props = {
  /** Số thứ tự món (1-based) — đặt tên ô cho trình đọc màn hình. */
  index: number;
  options: readonly KeyItemOption[];
  /** itemRef đã chọn ở các dòng khác — không gợi ý lại. */
  taken: ReadonlySet<string>;
  /** Món đang chọn ở dòng này ("" = chưa chọn). */
  selectedKey: string;
  selectedName: string;
  /** Ảnh đại diện của món đang chọn — hiện đầu ô khi ô đóng. */
  selectedImage?: string | undefined;
  loading?: boolean;
  /** Có dòng "gõ tay" ở cuối danh sách (chỉ hệ GNPM). */
  allowManual?: boolean;
  onPick: (option: KeyItemOption) => void;
  onManual?: () => void;
};

const MANUAL = "__manual__";

export function KeyItemPicker({
  index,
  options,
  taken,
  selectedKey,
  selectedName,
  selectedImage,
  loading = false,
  allowManual = false,
  onPick,
  onManual,
}: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listId = useId();

  const free = options.filter((o) => o.key === selectedKey || !taken.has(o.key));
  const typed = query.trim() !== "";
  // Chưa gõ ⇒ cả danh sách; gõ rồi ⇒ khớp thật trước, gần giống sau.
  const hits: SearchHit<KeyItemOption>[] = typed
    ? matchPackages(query, free, 30)
    : free.map((item) => ({ item, fuzzy: false }));
  const rows: (SearchHit<KeyItemOption> | typeof MANUAL)[] = allowManual ? [...hits, MANUAL] : hits;
  const firstFuzzy = hits.findIndex((h) => h.fuzzy);

  const close = () => {
    setOpen(false);
    setQuery("");
    setHighlight(0);
  };
  const choose = (i: number) => {
    const row = rows[i];
    if (row === undefined) return;
    if (row === MANUAL) onManual?.();
    else onPick(row.item);
    close();
  };

  return (
    <div className="relative min-w-0">
      {selectedKey && !open ? (
        // Món đã chọn: ảnh đại diện đứng chỗ kính lúp; mở ô ra gõ thì về kính lúp.
        <ServiceThumb src={selectedImage} className="pointer-events-none absolute left-1.5 top-1/2 size-8 -translate-y-1/2" />
      ) : (
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
      )}
      <Input
        role="combobox"
        aria-label={`Món ${index}`}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && rows[highlight] !== undefined ? `${listId}-${highlight}` : undefined}
        placeholder={selectedKey ? selectedName : "Bấm để chọn, hoặc gõ tên / mã món…"}
        className={selectedKey && !open ? "pl-11" : "pl-8"}
        value={open ? query : selectedName}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        // Rời ô là đóng; dòng gợi ý dùng onMouseDown nên vẫn kịp nhận bấm.
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            close();
            return;
          }
          if (!open) {
            if (e.key === "ArrowDown") setOpen(true);
            return;
          }
          if (rows.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => (h + 1) % rows.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => (h - 1 + rows.length) % rows.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            choose(highlight);
          }
        }}
      />

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
          {loading && options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Đang tải danh sách món…</p>
          ) : hits.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {typed ? `Không có món nào giống “${query.trim()}”.` : "Chưa có món nào trong danh sách."}
            </p>
          ) : (
            <p className="flex items-center justify-between border-b bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
              <span>
                {typed
                  ? `Gợi ý gần giống nhất — ${hits.filter((h) => !h.fuzzy).length} món có chữ “${query.trim()}”`
                  : `${hits.length} món — gõ để lọc`}
              </span>
              <span className="hidden md:inline">↑↓ chọn · Enter lấy</span>
            </p>
          )}
          <ul id={listId} role="listbox" aria-label={`Danh sách món ${index}`} className="max-h-80 overflow-y-auto">
            {rows.map((row, i) => {
              if (row === MANUAL) {
                return (
                  <li
                    key={MANUAL}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === highlight}
                    className={cn(
                      "cursor-pointer border-t px-3 py-2 text-sm text-primary",
                      i === highlight && "bg-muted/60",
                    )}
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(i);
                    }}
                  >
                    ✎ Gõ tay món chưa có trong danh sách
                  </li>
                );
              }
              const o = row.item;
              const prev = rows[i - 1];
              // Chưa gõ: tiêu đề mỗi khi sang nhóm mới; gõ rồi: tách nhóm "gần giống".
              const header = typed
                ? i === firstFuzzy
                  ? "Gần giống (lệch 1 chữ)"
                  : null
                : prev === undefined || prev === MANUAL || prev.item.group !== o.group
                  ? o.group
                  : null;
              return (
                <Fragment key={o.key}>
                  {header && (
                    <li role="presentation" className="border-t bg-muted/40 px-3 py-1 text-[11px] text-muted-foreground first:border-t-0">
                      {header}
                    </li>
                  )}
                  <li
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === highlight}
                    className={cn("flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm", i === highlight && "bg-muted/60")}
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(i);
                    }}
                  >
                    <ServiceThumb src={o.imageUrl} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{o.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {typed ? `${o.group} · ` : ""}
                        {o.hint ? `${o.hint} · ` : ""}mã {o.code}
                      </span>
                    </span>
                    {o.key === selectedKey ? (
                      <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-primary-foreground">
                        đang chọn
                      </span>
                    ) : null}
                  </li>
                </Fragment>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
